/**
 * Go To Segment Dialog
 * Allows quick navigation to specific segments
 * 
 * Location: src/components/cat/GoToSegmentDialog.tsx
 */

import React, { useState, useEffect, useRef } from 'react'
import { X, ArrowRight } from 'lucide-react'
import Button from '@/components/common/Button'

interface GoToSegmentDialogProps {
  isOpen: boolean
  onClose: () => void
  onGoToSegment: (segmentNumber: number) => void
  totalSegments: number
  currentSegment: number
}

const GoToSegmentDialog: React.FC<GoToSegmentDialogProps> = ({
  isOpen,
  onClose,
  onGoToSegment,
  totalSegments,
  currentSegment
}) => {
  const [segmentNumber, setSegmentNumber] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset and focus when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSegmentNumber('')
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const num = parseInt(segmentNumber, 10)
    
    if (isNaN(num) || num < 1 || num > totalSegments) {
      alert(`Please enter a valid segment number (1-${totalSegments})`)
      return
    }

    onGoToSegment(num)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Go to Segment</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Segment Number
              </label>
              <input
                ref={inputRef}
                type="number"
                min={1}
                max={totalSegments}
                value={segmentNumber}
                onChange={(e) => setSegmentNumber(e.target.value)}
                placeholder={`Enter 1-${totalSegments}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-600 focus:border-transparent"
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Current segment:</span>
                <span className="font-semibold text-gray-900">{currentSegment}</span>
              </div>
              <div className="flex justify-between">
                <span>Total segments:</span>
                <span className="font-semibold text-gray-900">{totalSegments}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<ArrowRight className="h-4 w-4" />}
              disabled={!segmentNumber}
            >
              Go to Segment
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default GoToSegmentDialog